/**
 * VoiceActivityDetector - Detects when user starts/stops speaking
 *
 * Uses Web Audio API AnalyserNode to monitor audio levels and detect silence.
 * Designed for the Q&A Practice feature where Murphy asks questions and
 * automatically advances when the user stops speaking.
 */

export const VAD_STATES = {
    IDLE: 'idle',
    LISTENING: 'listening',
    SPEECH_DETECTED: 'speech_detected',
    SILENCE_DETECTED: 'silence_detected',
    PAUSED: 'paused',
    STOPPED: 'stopped'
};

export class VoiceActivityDetector {
    /**
     * @param {AudioContext} audioContext - Existing AudioContext to use
     * @param {Object} options - Configuration options
     * @param {number} options.silenceThreshold - dB level below which is considered silence (default: -50)
     * @param {number} options.silenceDuration - ms of silence before triggering (default: 2000)
     * @param {number} options.noSpeechTimeout - ms before timing out if no speech detected (default: 30000)
     * @param {number} options.minSpeechDuration - minimum ms of speech before silence detection starts (default: 500)
     */
    constructor(audioContext, options = {}) {
        this.audioContext = audioContext;
        this.silenceThreshold = options.silenceThreshold ?? -50;
        this.silenceDuration = options.silenceDuration ?? 2000;
        this.noSpeechTimeout = options.noSpeechTimeout ?? 30000;
        this.minSpeechDuration = options.minSpeechDuration ?? 500;

        // Internal state
        this.state = VAD_STATES.IDLE;
        this.analyser = null;
        this.sourceNode = null;
        this.animationFrameId = null;
        this.silenceStartTime = null;
        this.speechStartTime = null;
        this.listeningStartTime = null;
        this.noSpeechTimeoutId = null;
        this.hasSpeechOccurred = false;

        // Callbacks
        this.callbacks = {
            onSpeechStart: null,
            onSpeechEnd: null,
            onSilenceDetected: null,
            onTimeout: null,
            onStateChange: null
        };

        // Audio analysis config
        this.fftSize = 2048;
        this.smoothingTimeConstant = 0.8;

        // Frequency data buffer
        this.frequencyData = null;
    }

    /**
     * Set callback functions
     * @param {Object} callbacks - Object with callback functions
     */
    setCallbacks(callbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }

    /**
     * Start listening for voice activity
     * @param {MediaStream} mediaStream - The microphone stream to analyze
     */
    start(mediaStream) {
        if (this.state !== VAD_STATES.IDLE && this.state !== VAD_STATES.STOPPED) {
            console.warn('VoiceActivityDetector: Already running, call stop() first');
            return;
        }

        try {
            // Create analyser node
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = this.fftSize;
            this.analyser.smoothingTimeConstant = this.smoothingTimeConstant;

            // Store reference for cleanup (important when using cloned streams)
            this.mediaStream = mediaStream;

            // Connect media stream to analyser
            this.sourceNode = this.audioContext.createMediaStreamSource(mediaStream);
            this.sourceNode.connect(this.analyser);

            // Initialize frequency data buffer
            this.frequencyData = new Float32Array(this.analyser.frequencyBinCount);

            // Reset state
            this.silenceStartTime = null;
            this.speechStartTime = null;
            this.hasSpeechOccurred = false;
            this.listeningStartTime = Date.now();

            // Start the no-speech timeout
            this.startNoSpeechTimeout();

            // Update state and start analysis loop
            this.setState(VAD_STATES.LISTENING);
            this.analyze();

            const tracks = mediaStream.getAudioTracks();
            console.log('VoiceActivityDetector: Started listening',
                `tracks=${tracks.length}`,
                `enabled=${tracks[0]?.enabled}`,
                `readyState=${tracks[0]?.readyState}`,
                `ctxState=${this.audioContext.state}`);
        } catch (error) {
            console.error('VoiceActivityDetector: Failed to start', error);
            // We are always handed a clone we own. this.mediaStream is assigned
            // before the throwing calls above, so without this the clone's mic
            // track stays live for the life of the page.
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => track.stop());
                this.mediaStream = null;
            }
            this.setState(VAD_STATES.STOPPED);
        }
    }

    /**
     * Stop listening and cleanup resources
     */
    stop() {
        // Cancel animation frame
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Clear timeout
        this.clearNoSpeechTimeout();

        // Disconnect nodes
        if (this.sourceNode) {
            try {
                this.sourceNode.disconnect();
            } catch (e) {
                // Ignore disconnect errors
            }
            this.sourceNode = null;
        }

        // Stop cloned stream tracks to free mic references
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        this.analyser = null;
        this.frequencyData = null;

        this.setState(VAD_STATES.STOPPED);
        console.log('VoiceActivityDetector: Stopped');
    }

    /**
     * Pause voice detection (e.g., while Murphy is speaking)
     */
    pause() {
        if (this.state === VAD_STATES.LISTENING || this.state === VAD_STATES.SPEECH_DETECTED) {
            this.previousState = this.state;
            this.setState(VAD_STATES.PAUSED);
            this.clearNoSpeechTimeout();
            console.log('VoiceActivityDetector: Paused');
        }
    }

    /**
     * Resume voice detection
     */
    resume() {
        if (this.state === VAD_STATES.PAUSED) {
            // Reset timing when resuming
            this.silenceStartTime = null;
            this.listeningStartTime = Date.now();

            // Only restart timeout if no speech has occurred yet
            if (!this.hasSpeechOccurred) {
                this.startNoSpeechTimeout();
            }

            this.setState(this.previousState || VAD_STATES.LISTENING);
            console.log('VoiceActivityDetector: Resumed');
        }
    }

    /**
     * Main analysis loop - runs via requestAnimationFrame
     */
    analyze() {
        if (this.state === VAD_STATES.STOPPED) {
            return;
        }

        // Skip analysis if paused
        if (this.state === VAD_STATES.PAUSED) {
            this.animationFrameId = requestAnimationFrame(() => this.analyze());
            return;
        }

        if (!this.analyser || !this.frequencyData) {
            return;
        }

        // Get frequency data in dB
        this.analyser.getFloatFrequencyData(this.frequencyData);

        // Calculate peak level in speech frequency range
        const peakLevel = this.calculateSpeechLevel();

        const isSpeaking = peakLevel > this.silenceThreshold;
        const now = Date.now();

        // Diagnostic: log levels every 3 seconds so we can verify audio is flowing
        if (!this._lastDiagTime || now - this._lastDiagTime > 3000) {
            this._lastDiagTime = now;
            console.log(`VoiceActivityDetector: peak=${peakLevel.toFixed(1)}dB, speaking=${isSpeaking}, threshold=${this.silenceThreshold}, ctxState=${this.audioContext.state}`);
        }

        if (isSpeaking) {
            // Speech detected
            if (!this.hasSpeechOccurred) {
                this.hasSpeechOccurred = true;
                this.speechStartTime = now;
                this.clearNoSpeechTimeout(); // Cancel the no-speech timeout

                if (this.callbacks.onSpeechStart) {
                    this.callbacks.onSpeechStart();
                }
            }

            this.silenceStartTime = null;

            if (this.state !== VAD_STATES.SPEECH_DETECTED) {
                this.setState(VAD_STATES.SPEECH_DETECTED);
            }
        } else {
            // Silence detected
            if (this.hasSpeechOccurred) {
                // Only track silence after speech has occurred
                if (!this.silenceStartTime) {
                    this.silenceStartTime = now;
                }

                const silenceDurationMs = now - this.silenceStartTime;
                const speechDurationMs = this.speechStartTime ? (this.silenceStartTime - this.speechStartTime) : 0;

                // Check if we've had enough speech before triggering silence detection
                if (speechDurationMs >= this.minSpeechDuration && silenceDurationMs >= this.silenceDuration) {
                    this.setState(VAD_STATES.SILENCE_DETECTED);

                    if (this.callbacks.onSpeechEnd) {
                        this.callbacks.onSpeechEnd();
                    }

                    if (this.callbacks.onSilenceDetected) {
                        this.callbacks.onSilenceDetected({
                            speechDuration: speechDurationMs,
                            silenceDuration: silenceDurationMs
                        });
                    }

                    // Stop after silence is detected
                    this.stop();
                    return;
                }
            }
        }

        // Continue analysis loop
        this.animationFrameId = requestAnimationFrame(() => this.analyze());
    }

    /**
     * Calculate peak level in speech frequency range.
     * Uses peak (max) rather than average because averaging all bins from
     * 85-4000 Hz dilutes the speech signal — most bins have no speech energy,
     * dragging the average below the threshold even during active speech.
     * @returns {number} Peak dB level in speech range
     */
    calculateSpeechLevel() {
        if (!this.analyser || !this.frequencyData) {
            return -Infinity;
        }

        const sampleRate = this.audioContext.sampleRate;
        const binWidth = sampleRate / this.fftSize;

        // Focus on speech frequency range: 85Hz - 4000Hz
        // This covers fundamental frequencies (85-255 Hz) and harmonics
        const minFreq = 85;
        const maxFreq = 4000;
        const minBin = Math.floor(minFreq / binWidth);
        const maxBin = Math.min(Math.ceil(maxFreq / binWidth), this.frequencyData.length - 1);

        let peak = -Infinity;

        for (let i = minBin; i <= maxBin; i++) {
            const value = this.frequencyData[i];
            if (value > peak && !isNaN(value)) {
                peak = value;
            }
        }

        return peak;
    }

    /**
     * Start the no-speech timeout
     */
    startNoSpeechTimeout() {
        this.clearNoSpeechTimeout();

        this.noSpeechTimeoutId = setTimeout(() => {
            if (this.state === VAD_STATES.LISTENING && !this.hasSpeechOccurred) {
                console.log('VoiceActivityDetector: No speech timeout triggered');

                if (this.callbacks.onTimeout) {
                    this.callbacks.onTimeout({
                        reason: 'no_speech',
                        duration: this.noSpeechTimeout
                    });
                }

                this.stop();
            }
        }, this.noSpeechTimeout);
    }

    /**
     * Clear the no-speech timeout
     */
    clearNoSpeechTimeout() {
        if (this.noSpeechTimeoutId) {
            clearTimeout(this.noSpeechTimeoutId);
            this.noSpeechTimeoutId = null;
        }
    }

    /**
     * Update internal state and notify listeners
     * @param {string} newState - New state from VAD_STATES
     */
    setState(newState) {
        const oldState = this.state;
        this.state = newState;

        if (this.callbacks.onStateChange && oldState !== newState) {
            this.callbacks.onStateChange(newState, oldState);
        }
    }

    /**
     * Get current state
     * @returns {string} Current VAD state
     */
    getState() {
        return this.state;
    }

    /**
     * Check if speech has been detected in this session
     * @returns {boolean}
     */
    hasSpeech() {
        return this.hasSpeechOccurred;
    }

    /**
     * Get current audio level (for visualization)
     * @returns {number} Current average dB level in speech range
     */
    getCurrentLevel() {
        if (this.state === VAD_STATES.STOPPED || this.state === VAD_STATES.IDLE) {
            return -Infinity;
        }
        return this.calculateSpeechLevel();
    }
}

export default VoiceActivityDetector;
